import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'

import { Skeleton } from './components/ui'
import { useDocumentTitle } from './lib/documentTitle'
import { useSession } from './lib/session'
import { Account } from './pages/Account'
import { ChannelOverview } from './pages/ChannelOverview'
import { Spotlights } from './pages/Spotlights'
import { Stories } from './pages/Stories'
import { AdminUsers } from './pages/AdminUsers'
import { Login } from './pages/Login'
import { NoAccess } from './pages/NoAccess'
import { Connections } from './pages/Connections'
import { Legacy } from './pages/Legacy'
import { Matches } from './pages/Matches'
import { RawData } from './pages/RawData'
import { ForgotPassword, SetPassword, VerifyEmail } from './pages/PasswordFlows'
import { Register } from './pages/Register'
import { Revenue } from './pages/Revenue'
import { AppShell } from './shell/AppShell'
import { useTheme } from './lib/theme'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // A 401 means the session went away; retrying it just delays the redirect.
      retry: (failureCount, error) =>
        !(error instanceof Error && 'status' in error && (error as { status: number }).status === 401) &&
        failureCount < 2,
      staleTime: 30 * 1000,
      refetchOnWindowFocus: false,
    },
  },
})

/** Client-side guard. **Convenience only** — every protected route also carries
 *  a server-side dependency, and Stage 8 tests those directly. */
function RequireSession({ children }: { children: React.ReactNode }) {
  const { me, loading } = useSession()
  const location = useLocation()

  if (loading) {
    return (
      <div className="px-6 py-8" style={{ maxWidth: '24rem' }}>
        <Skeleton rows={3} />
      </div>
    )
  }
  if (!me) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />
  }
  return <>{children}</>
}

/** `/` lands on the first section the user can actually open. */
function Landing() {
  const { me } = useSession()
  const first = me?.sections.find((section) =>
    ['stories', 'spotlights', 'channels', 'revenue'].includes(section),
  )
  if (first) return <Navigate to={`/${first}`} replace />
  return <NoAccess />
}

export function App() {
  // The stamp is applied by `lib/theme` on import, before React's first paint —
  // so this subscribes the shell to changes rather than doing the applying. The
  // picker lives in the user menu, which only renders behind a login; this keeps
  // the login and reset pages themed too.
  useTheme()
  // The tab name follows the route. One call, above `Routes`, because the title
  // is a property of the URL rather than of whichever page happens to render it
  // — and a page-by-page `useEffect` is the version that drifts.
  useDocumentTitle()

  return (
    <QueryClientProvider client={queryClient}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<SetPassword mode="reset" />} />
        <Route path="/set-password" element={<SetPassword mode="invite" />} />
        <Route path="/verify-email" element={<VerifyEmail />} />

        <Route
          element={
            <RequireSession>
              <AppShell />
            </RequireSession>
          }
        >
          <Route path="/" element={<Landing />} />
          <Route path="/stories" element={<Stories />} />
          <Route path="/spotlights" element={<Spotlights />} />
          <Route path="/channels" element={<ChannelOverview />} />
          <Route path="/revenue" element={<Revenue />} />
          <Route path="/raw-data" element={<RawData />} />
          <Route path="/connections" element={<Connections />} />
          {/* A sub-route rather than a tab: it is a working screen someone is
              sent a link to, not a view of the same data. Spec §5.3a. */}
          <Route path="/connections/matches" element={<Matches />} />
          {/* Deliberately not a sidebar section — reached from Connections.
              DESIGN-SYSTEM.md §7. */}
          <Route path="/legacy" element={<Legacy />} />
          <Route path="/admin/users" element={<AdminUsers />} />
          <Route path="/account" element={<Account />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </QueryClientProvider>
  )
}
